/**
 * Permission Utilities for Role-Based Access Control
 *
 * This file contains all permission check functions for the multi-role system.
 * Permissions are additive: having ANY role with access grants permission.
 *
 * See ROLE_DEFINITIONS.md for complete access control matrix.
 */

import {
  UserRole,
  DashboardType,
  DashboardOption,
  HRDocumentType,
  ROLE_DEFAULT_DASHBOARD,
  DASHBOARD_OPTIONS,
  isCredentialDocument,
  isComplianceDocument
} from '../types/roles';

/**
 * User interface with multi-role support
 * This should match the UserProfile in FirebaseAuthService
 */
export interface RoleUser {
  uid: string;
  roles: UserRole[];
  primaryRole: UserRole;
  email?: string;
  displayName?: string;
}

/**
 * Check if user has a specific role
 */
export function hasRole(user: RoleUser | null, role: UserRole): boolean {
  if (!user) return false;
  return user.roles.includes(role);
}

/**
 * Check if user has ANY of the specified roles
 */
export function hasAnyRole(user: RoleUser | null, roles: UserRole[]): boolean {
  if (!user) return false;
  return roles.some(role => user.roles.includes(role));
}

/**
 * Check if user has ALL of the specified roles
 */
export function hasAllRoles(user: RoleUser | null, roles: UserRole[]): boolean {
  if (!user) return false;
  return roles.every(role => user.roles.includes(role));
}

// ============================================================================
// HR DOCUMENT ACCESS PERMISSIONS
// ============================================================================

/**
 * Check if user can access HR compliance documents for a specific employee
 *
 * Access Rules:
 * - System Admin: All documents
 * - HR Admin: All documents
 * - Employee: Own documents only
 * - All others: No access
 */
export function canAccessHRCompliance(
  user: RoleUser | null,
  targetEmployeeId?: string
): boolean {
  if (!user) return false;

  // System Admin, Clinical Director, and HR Admin can access all compliance documents
  if (hasAnyRole(user, ['system_admin', 'clinical_director', 'hr_admin'])) {
    return true;
  }

  // Users can always access their own HR compliance documents
  if (targetEmployeeId && user.uid === targetEmployeeId) {
    return true;
  }

  return false;
}

/**
 * Check if user can view professional credentials for any provider
 *
 * Access Rules:
 * - System Admin: Yes
 * - HR Admin: Yes
 * - Clinical Director: Yes
 * - Provider: Yes (can see other providers' credentials)
 * - Administrative Staff: No
 * - Client: No
 */
export function canViewProviderCredentials(user: RoleUser | null): boolean {
  if (!user) return false;

  return hasAnyRole(user, [
    'system_admin',
    'hr_admin',
    'clinical_director',
    'provider'
  ]);
}

/**
 * Check if user can access a specific HR document
 *
 * Combines compliance and credential checks based on document type
 */
export function canAccessHRDocument(
  user: RoleUser | null,
  documentType: HRDocumentType,
  documentOwnerId: string
): boolean {
  if (!user) return false;

  // Credentials (semi-public): viewable by providers, directors, HR
  if (isCredentialDocument(documentType)) {
    // Can view own credentials
    if (user.uid === documentOwnerId) return true;

    // Or have credential viewing permissions
    return canViewProviderCredentials(user);
  }

  // Compliance (restricted): viewable by owner, HR admin, system admin only
  if (isComplianceDocument(documentType)) {
    return canAccessHRCompliance(user, documentOwnerId);
  }

  return false;
}

/**
 * Check if user can assign HR documents to employees
 *
 * Access Rules:
 * - System Admin: Yes
 * - HR Admin: Yes
 * - All others: No
 */
export function canAssignHRDocuments(user: RoleUser | null): boolean {
  if (!user) return false;
  return hasAnyRole(user, ['system_admin', 'clinical_director', 'hr_admin']);
}

/**
 * Check if user can approve/review submitted HR documents
 *
 * Access Rules:
 * - System Admin: Yes
 * - HR Admin: Yes
 * - All others: No
 */
export function canReviewHRDocuments(user: RoleUser | null): boolean {
  if (!user) return false;
  return hasAnyRole(user, ['system_admin', 'clinical_director', 'hr_admin']);
}

// ============================================================================
// PATIENT DATA ACCESS PERMISSIONS
// ============================================================================

/**
 * Check if user can access patient clinical data
 *
 * Access Rules:
 * - System Admin: All patients
 * - Clinical Director: All patients (supervision)
 * - Provider: Own patients only
 * - Client: Own data only
 * - All others: No access
 */
export function canAccessPatientData(
  user: RoleUser | null,
  patientId: string,
  assignedProviderId?: string
): boolean {
  if (!user) return false;

  // System Admin and Clinical Director can access all patient data
  if (hasAnyRole(user, ['system_admin', 'clinical_director'])) {
    return true;
  }

  // Providers can only access their assigned patients
  if (hasRole(user, 'provider') && assignedProviderId === user.uid) {
    return true;
  }

  // Clients can only access their own data
  if (hasRole(user, 'client') && user.uid === patientId) {
    return true;
  }

  return false;
}

/**
 * Check if user can create/edit session notes
 *
 * Access Rules:
 * - System Admin: Yes
 * - Clinical Director: Yes (for supervision)
 * - Provider: Own sessions only
 * - All others: No
 */
export function canEditSessionNotes(
  user: RoleUser | null,
  sessionProviderId?: string
): boolean {
  if (!user) return false;

  // System Admin and Clinical Director can edit all notes
  if (hasAnyRole(user, ['system_admin', 'clinical_director'])) {
    return true;
  }

  // Providers can only edit their own session notes
  if (hasRole(user, 'provider') && sessionProviderId === user.uid) {
    return true;
  }

  return false;
}

// ============================================================================
// ANALYTICS & BILLING PERMISSIONS
// ============================================================================

/**
 * Check if user can access analytics and billing data
 *
 * Access Rules:
 * - System Admin: All data
 * - Clinical Director: All data
 * - Provider: Own data only
 * - Administrative Staff: All data (for billing)
 * - All others: No access
 */
export function canAccessAnalytics(
  user: RoleUser | null,
  targetProviderId?: string
): boolean {
  if (!user) return false;

  // System Admin, Clinical Director, and Admin Staff can access all analytics
  if (hasAnyRole(user, ['system_admin', 'clinical_director', 'administrative_staff'])) {
    return true;
  }

  // Providers can access their own analytics
  if (hasRole(user, 'provider') && (!targetProviderId || targetProviderId === user.uid)) {
    return true;
  }

  return false;
}

/**
 * Check if user can manage billing and invoices
 *
 * Access Rules:
 * - System Admin: Yes
 * - Clinical Director: Yes
 * - Administrative Staff: Yes
 * - All others: No
 */
export function canManageBilling(user: RoleUser | null): boolean {
  if (!user) return false;
  return hasAnyRole(user, ['system_admin', 'clinical_director', 'administrative_staff']);
}

// ============================================================================
// SCHEDULE MANAGEMENT PERMISSIONS
// ============================================================================

/**
 * Check if user can manage schedules
 *
 * Access Rules:
 * - System Admin: All schedules
 * - Clinical Director: All schedules
 * - Provider: Own schedule only
 * - Administrative Staff: All schedules
 * - Client: Own appointments only
 * - All others: No access
 */
export function canManageSchedule(
  user: RoleUser | null,
  targetUserId?: string
): boolean {
  if (!user) return false;

  // System Admin, Clinical Director, and Admin Staff can manage all schedules
  if (hasAnyRole(user, ['system_admin', 'clinical_director', 'administrative_staff'])) {
    return true;
  }

  // Providers and Clients can manage their own schedules
  if (!targetUserId || targetUserId === user.uid) {
    return hasAnyRole(user, ['provider', 'client']);
  }

  return false;
}

// ============================================================================
// ROLE MANAGEMENT PERMISSIONS
// ============================================================================

/**
 * Check if user can assign roles to other users
 *
 * Access Rules:
 * - System Admin: Yes
 * - All others: No
 */
export function canAssignRoles(user: RoleUser | null): boolean {
  if (!user) return false;
  return hasAnyRole(user, ['system_admin', 'clinical_director']);
}

/**
 * Check if user can create new user accounts
 *
 * Access Rules:
 * - System Admin: Yes
 * - HR Admin: Yes (for employee onboarding)
 * - Administrative Staff: Yes (for client intake)
 * - All others: No
 */
export function canCreateUsers(user: RoleUser | null): boolean {
  if (!user) return false;
  return hasAnyRole(user, ['system_admin', 'clinical_director', 'hr_admin', 'administrative_staff']);
}

// ============================================================================
// DASHBOARD ACCESS PERMISSIONS
// ============================================================================

/**
 * Get available dashboards for user based on their roles
 */
export function getAvailableDashboards(user: RoleUser | null): DashboardOption[] {
  if (!user) return [];

  const dashboards: DashboardOption[] = [];

  // Map roles to dashboards
  if (hasRole(user, 'provider')) {
    dashboards.push({ id: 'provider', ...DASHBOARD_OPTIONS.provider });
  }

  if (hasRole(user, 'hr_admin')) {
    dashboards.push({ id: 'hr', ...DASHBOARD_OPTIONS.hr });
  }

  if (hasRole(user, 'clinical_director')) {
    dashboards.push({ id: 'clinical', ...DASHBOARD_OPTIONS.clinical });
  }

  if (hasRole(user, 'administrative_staff')) {
    dashboards.push({ id: 'admin', ...DASHBOARD_OPTIONS.admin });
  }

  if (hasRole(user, 'client')) {
    dashboards.push({ id: 'client', ...DASHBOARD_OPTIONS.client });
  }

  return dashboards;
}

/**
 * Get default dashboard for user based on primary role
 */
export function getDefaultDashboard(user: RoleUser | null): DashboardType | null {
  if (!user) return null;
  return ROLE_DEFAULT_DASHBOARD[user.primaryRole];
}

/**
 * Check if user can access a specific dashboard
 */
export function canAccessDashboard(
  user: RoleUser | null,
  dashboard: DashboardType
): boolean {
  if (!user) return false;

  const availableDashboards = getAvailableDashboards(user);
  return availableDashboards.some(d => d.id === dashboard);
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Check if user is an admin (System Admin or HR Admin)
 */
export function isAdmin(user: RoleUser | null): boolean {
  if (!user) return false;
  return hasAnyRole(user, ['system_admin', 'hr_admin']);
}

/**
 * Check if user is a clinical staff member (can access patient data)
 */
export function isClinicalStaff(user: RoleUser | null): boolean {
  if (!user) return false;
  return hasAnyRole(user, ['system_admin', 'clinical_director', 'provider']);
}

/**
 * Check if user has multiple roles (needs dashboard switcher)
 */
export function hasMultipleRoles(user: RoleUser | null): boolean {
  if (!user) return false;
  return user.roles.length > 1;
}

/**
 * Get user's role priority for display (highest priority role)
 * Priority: system_admin > clinical_director > hr_admin > provider > administrative_staff > client
 */
export function getPrimaryDisplayRole(user: RoleUser | null): UserRole | null {
  if (!user || user.roles.length === 0) return null;

  const rolePriority: UserRole[] = [
    'system_admin',
    'clinical_director',
    'hr_admin',
    'provider',
    'administrative_staff',
    'client'
  ];

  for (const role of rolePriority) {
    if (hasRole(user, role)) {
      return role;
    }
  }

  return user.roles[0];
}
